import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Breadcrumbs,
    Link,
    Button,
    IconButton,
    Grid,
    Paper,
    Divider,
    Rating,
    Tabs,
    Tab,
    Avatar,
    TextField,
    Snackbar,
    Alert,
} from "@mui/material";

import { FaHeart, FaRegHeart, FaChevronRight } from "react-icons/fa";

import { useNavigate, useParams } from "react-router-dom";

import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";

import { useAuth } from "../context/AuthContext";

const PageProduto = () => {

    const navigate = useNavigate();

    const { addToCart } = useAuth();
    const { id } = useParams();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedFlavor, setSelectedFlavor] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [tab, setTab] = useState(0);

    useEffect(() => {
      if (!id) {
        navigate("/produtos");
        return;
      }

      loadProduct();
    }, [id]);

    async function loadProduct() {

      try {

        const snap = await get(ref(rtdb, `products/${id}`));

        if (!snap.exists()) {
          navigate("/produtos");
          return;
        }

        const data = snap.val();

        setProduct({
          ...data,
          id,
          imagens: data.imagens || [],
          variacoes: data.variacoes || [],
          specs: data.specs || [],
          precos: data.precos || {},
        });

        setSelectedImage(data.imagens?.[0] || null);

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }

    }

    if (loading) {
      return (
        <Container sx={{ mt: 10 }}>
          <Typography>Carregando produto...</Typography>
        </Container>
      );
    }

    if (!product) {
      return (
        <Container sx={{ mt: 10 }}>
          <Typography>Produto não encontrado.</Typography>
        </Container>
      );
    }

    const hasStock = Boolean(product?.precos?.normal);
    const hasFlavorSelected = Boolean(selectedFlavor);
    const hasValidQuantity = Number.isInteger(quantity) && quantity > 0;

    const canBuy = hasStock && hasFlavorSelected && hasValidQuantity;

    return (
        <>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 12 }}>

                {/* BREADCRUMB */}
                <Breadcrumbs
                    separator={<FaChevronRight size={12} color="var(--azul)" />}
                    sx={{ mb: 4, fontSize: 14 }}
                >
                    <Link underline="hover" color="var(--cinza)" href="/">
                        Home
                    </Link>
                    <Link underline="hover" color="var(--cinza)" href="/produtos">
                        Produtos
                    </Link>
                    <Typography color="text.primary">{product.nome}</Typography>
                </Breadcrumbs>

                {/* GRID PRINCIPAL */}
                <Grid
                    container
                    spacing={6}
                    alignItems="flex-start"
                    sx={{ justifyContent: "center" }}
                >

                    {/* GALERIA */}
                    <Grid item xs={12} md={5}>
                        <Paper
                            sx={{
                                width: "100%",
                                height: 450,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "20px",
                                overflow: "hidden",
                                mb: 2,
                                boxShadow: "0 5px 18px rgba(0,0,0,0.12)",
                            }}
                        >
                            <img
                                src={selectedImage}
                                alt={product.nome}
                                style={{
                                    width: "80%",
                                    height: "80%",
                                    objectFit: "contain",
                                }}
                            />
                        </Paper>

                        {/* Miniaturas */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            {product.imagens.length > 0 && product.imagens.map((img, index) => (
                                <Paper
                                    key={index}
                                    onClick={() => setSelectedImage(img)}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        padding: 1,
                                        borderRadius: "14px",
                                        cursor: "pointer",
                                        border:
                                            selectedImage === img
                                                ? "2px solid var(--azul)"
                                                : "2px solid transparent",
                                        transition: "0.25s",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                        },
                                    }}
                                >
                                    <img
                                        src={img}
                                        alt={`preview-${index}`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                        }}
                                    />
                                </Paper>
                            ))}
                        </Box>
                    </Grid>

                    {/* INFO */}
                    <Grid
                      item
                      xs={12}
                      md={7}
                      sx={{
                        maxWidth: 560,
                        width: "100%",
                      }}
                    >


                        {/* ID do produto */}
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "#999",
                                fontWeight: 600,
                                mt: 1,
                                mb: 0.5
                            }}
                        >
                            #{product.id}
                        </Typography>


                        {/* Título */}
                        <Typography sx={{ fontWeight: 700, fontSize: 30, color: "var(--cinza)" }}>
                            {product.nome}
                        </Typography>

                        {/* Avaliações */}
                        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                            <Rating value={product.rating} precision={0.1} readOnly />
                            <Typography sx={{ ml: 1, color: "var(--cinza)" }}>
                                (2 avaliações)
                            </Typography>
                        </Box>



                        {/* Preço (com ou sem promoção) */}
                        {product.precos.isPromo ? (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: 20,
                                        color: "#888",
                                        textDecoration: "line-through",
                                        fontWeight: 600,
                                    }}
                                >
                                    {product.precos.normal}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: 34,
                                        color: "var(--azul)",
                                        mt: -0.5
                                    }}
                                >
                                    {product.precos.promo}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: 34,
                                    mt: 1,
                                    color: "var(--azul)",
                                }}
                            >
                                {product.precos.normal}
                              </Typography>
                        )}

                        <Typography
                            sx={{
                                fontSize: 15,
                                color: product.precos.normal ? "green" : "red",
                                mt: 1,
                                fontWeight: 600,
                            }}
                        >
                            {product.precos.normal ? "Em estoque" : "Esgotado"}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        {/* Sabores */}
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Disponíveis:</Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {product.variacoes.map((v, index) => (
                                <Paper
                                    key={index}
                                    onClick={() => setSelectedFlavor(v.nome)}
                                    sx={{
                                        padding: "8px 16px",
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        backgroundColor:
                                            selectedFlavor === v.nome
                                                ? "var(--azul)"
                                                : "rgba(30,43,78,0.06)",
                                        color:
                                            selectedFlavor === v.nome ? "white" : "var(--cinza)",
                                        fontWeight: selectedFlavor === v.nome ? 700 : 500,
                                        transition: "0.2s",
                                        "&:hover": {
                                            transform: "scale(1.07)",
                                        },
                                    }}
                                >
                                    {v.nome}
                                </Paper>
                            ))}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Quantidade */}
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Quantidade:</Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                                sx={{
                                    minWidth: 45,
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    color: "var(--cinza)",
                                    fontSize: 20,
                                    fontWeight: 700,
                                }}
                            >
                                -
                            </Button>

                            <TextField
                                value={quantity}
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: 70,
                                    textAlign: "center",
                                    "& input": { textAlign: "center" },
                                }}
                            />

                            <Button
                                onClick={() => setQuantity(quantity + 1)}
                                sx={{
                                    minWidth: 45,
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    color: "var(--cinza)",
                                    fontSize: 20,
                                    fontWeight: 700,
                                }}
                            >
                                +
                            </Button>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* BOTÕES */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                              fullWidth
                              disabled={!canBuy}
                              sx={{
                                backgroundColor: canBuy ? "var(--azul)" : "#cfd4dc",
                                color: canBuy ? "white" : "#7a7a7a",
                                paddingY: 1.7,
                                fontWeight: 800,
                                borderRadius: "14px",
                                fontSize: 16,
                                cursor: canBuy ? "pointer" : "not-allowed",
                                "&:hover": canBuy
                                  ? { backgroundColor: "#112040" }
                                  : {},
                              }}
                              onClick={() => {
                                if (!canBuy) return;

                                addToCart({
                                  productId: product.id,
                                  flavor: selectedFlavor,
                                  quantity,
                                });

                                setToastMessage("Adicionado ao carrinho!");
                                setToastOpen(true);
                              }}
                            >
                              COMPRAR AGORA
                            </Button>

                            <IconButton
                                sx={{
                                    backgroundColor: "#fff",
                                    border: "2px solid #e2e2e2",
                                    borderRadius: "14px",
                                    width: 56,
                                    height: 56,
                                    "&:hover": {
                                        backgroundColor: "#f2f2f2",
                                    },
                                }}
                            >
                                <FaHeart size={22} color="var(--azul)" />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                {/* TABS */}
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    sx={{ mt: 6 }}
                    textColor="var(--cinza)"
                    indicatorColor="primary"
                >
                    <Tab label="Descrição" />
                    <Tab label="Avaliações" />
                    <Tab label="Recomendações" />
                </Tabs>

                <Divider sx={{ mb: 4 }} />

                {/* CONTEÚDO DAS TABS */}
                {tab === 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 16, color: "#444", lineHeight: 1.7 }}>
                            {product.descricao}
                        </Typography>

                        <Typography
                            sx={{ fontWeight: 700, fontSize: 18, mt: 4, mb: 1 }}
                        >
                            Especificações Técnicas
                        </Typography>

                        <Paper sx={{ p: 3, borderRadius: "16px" }}>
                            {product.specs.map((s, i) => (
                              <Box key={i} sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography fontWeight={600}>{s.key}</Typography>
                                <Typography>{s.value}</Typography>
                              </Box>
                            ))}
                        </Paper>
                    </Box>
                )}

                {tab === 1 && (
                    <Box>
                        {product.comments.map((c, i) => (
                            <Paper
                                key={i}
                                sx={{
                                    p: 3,
                                    mb: 2,
                                    borderRadius: "16px",
                                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <Avatar>{c.user[0]}</Avatar>
                                    <Typography sx={{ ml: 2, fontWeight: 600 }}>
                                        {c.user}
                                    </Typography>
                                </Box>

                                <Rating value={c.stars} readOnly />

                                <Typography sx={{ mt: 1, mb: 2 }}>{c.text}</Typography>

                                {c.picture && (
                                    <img
                                        src={c.picture}
                                        alt="review"
                                        style={{
                                            width: 120,
                                            borderRadius: "10px",
                                        }}
                                    />
                                )}
                            </Paper>
                        ))}
                    </Box>
                )}

                {tab === 2 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 2, fontWeight: 700 }}>
                            Produtos que você pode gostar:
                        </Typography>

                        <Grid container spacing={3}>
                            {[1, 1, 1].map((_, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Paper
                                        sx={{
                                            p: 3,
                                            borderRadius: "16px",
                                            textAlign: "center",
                                        }}
                                    >
                                        <img
                                            src={selectedImage}
                                            alt="recomendado"
                                            style={{ width: "80%", objectFit: "contain" }}
                                        />
                                        <Typography sx={{ mt: 2, fontWeight: 700 }}>
                                            Pod Ignite V120
                                        </Typography>
                                        <Typography sx={{ color: "var(--azul)", fontWeight: 700 }}>
                                            R$ 111,00
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Container>

            {/* BOTÃO FIXO MOBILE */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    backgroundColor: "#fff",
                    display: { xs: "flex", md: "none" },
                    boxShadow: "0 -3px 10px rgba(0,0,0,0.15)",
                    padding: 2,
                    zIndex: 999,
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography sx={{ fontWeight: 700, color: "var(--azul)" }}>
                    R$ {product.precos.normal}
                </Typography>

                <Button
                  disabled={!canBuy}
                  sx={{
                    backgroundColor: canBuy ? "var(--azul)" : "#cfd4dc",
                    color: canBuy ? "white" : "#7a7a7a",
                    paddingX: 3,
                    paddingY: 1.2,
                    fontWeight: 700,
                    borderRadius: "12px",
                  }}
                >
                  Comprar
                </Button>
            </Box>
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                    anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity="success">{toastMessage}</Alert>
            </Snackbar>
        </>
    );
};

export default PageProduto;